import React, { useEffect, useState } from 'react';
import EditIcon from '../confirm/EditIcon';
import Modal from './Modal';
import XcomID from './XcomID';

/**
 * Displays a Modal that allows the user to supply a resubmittal/recollection ID.
 *
 * @param resubmittalId The supplied resubmittal Id
 * @param onChange A function to call when a new value is saved
 * @param onClose A function to call when the modal is closed
 */
export default function ResubmittalPrompt({ resubmittalId, onChange, onClose }) {
	const [resubValue, setResubValue] = useState(resubmittalId === 'Not Provided' ? '' : resubmittalId ?? '');
	const [isRecollection, setIsRecollection] = useState(true);

	/**
	 * Clear the resubmittal value if the checkbox is unchecked.
	 * Reset to the initial value if the checkbox is checked.
	 */
	useEffect(() => {
		if (!isRecollection) {
			setResubValue('');
		} else {
			setResubValue(resubmittalId === 'Not Provided' ? '' : resubmittalId ?? '');
		}
	}, [isRecollection, resubmittalId]);

	const toggleIsRecollection = (event) => {
		setIsRecollection(event.target.checked);
	};
	const handleResubValueChange = (event) => {
		setResubValue(event.target.value);
	};

	/**
	 * Pass the value to the parent through the given onChange method.
	 * A blank value will be converted to "Not Provided".
	 */
	const handleSave = (event) => {
		if (!isRecollection) {
			onChange('');
		} else {
			onChange(resubValue || 'Not Provided');
		}
	};

	return (
		<Modal
			id="resubmittal-modal"
			title="Order for Recollection"
			confirmAction="Save"
			cancelAction="Cancel"
			onSave={handleSave}
			modalHidden={onClose}
		>
			<div>
				<div className="checkbox">
					<label>
						<input type="checkbox" checked={isRecollection} onChange={toggleIsRecollection} /> Collection
						pack is needed to recollect samples for an open order
					</label>
				</div>

				{isRecollection && (
					<>
						<label>Please enter the QA, STAR, or original order number, if available.</label>
						<div className="small">
							The QA or STAR number may have been emailed to you.{' '}
							<a href="/mygdx/webreporting" target="_blank" title="View previous orders">
								View previous order numbers <span className="fa fa-external-link pr-5"></span>
							</a>
						</div>
						<div className="form-group space-top">
							<input
								className="form-control"
								type="text"
								placeholder="QA, STAR, or accession number"
								value={resubValue}
								onChange={handleResubValueChange}
								maxLength={32}
							/>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
}

/**
 * Shows either the "Is this order a recollection?" link or the recollection order ID.
 */
export const ResubmittalLink = ({ resubmittalId, onClick, disableEdit }) => {
	// Do not display anything if there is no resubmittal Id and edits are not allowed
	if (disableEdit && !resubmittalId) {
		return null;
	}

	if (resubmittalId) {
		return (
			<>
				<XcomID value={resubmittalId} />
				{!disableEdit && <EditIcon title="Recollection Order" onClick={onClick} />}
			</>
		);
	} else {
		return (
			<>
				<span style={{marginRight: '10px'}}>
					<strong>
						Is this order a recollection?
					</strong>
				</span>
				<span>No</span>
				<EditIcon title="Recollection Order" onClick={onClick} />
			</>
		);
	}
};
